import React from 'react';
import { Loader2, Sparkles, Satellite, Building2 } from 'lucide-react';

interface PortalLoadingSkeletonProps {
  theme?: 'office' | 'space' | 'general';
  message?: string;
}

export const PortalLoadingSkeleton: React.FC<PortalLoadingSkeletonProps> = ({
  theme = 'general',
  message
}) => {
  const isSpace = theme === 'space';
  const isOffice = theme === 'office';

  const accentColor = isSpace ? 'cyan' : isOffice ? 'violet' : 'amber';
  const icon = isSpace ? (
    <Satellite className="w-6 h-6 text-cyan-400 animate-spin" />
  ) : isOffice ? (
    <Building2 className="w-6 h-6 text-violet-400 animate-pulse" />
  ) : (
    <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
  );

  return (
    <div className="w-full min-h-[420px] rounded-2xl border border-slate-200/80 bg-white/70 backdrop-blur-md p-6 flex flex-col justify-center items-center space-y-6 shadow-sm">
      {/* Animated Icon & Spinner */}
      <div className="relative flex items-center justify-center">
        <div className={`w-16 h-16 rounded-2xl ${
          isSpace 
            ? 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-400' 
            : isOffice 
              ? 'bg-violet-500/10 border border-violet-500/30 text-violet-400' 
              : 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
        } flex items-center justify-center shadow-lg animate-pulse`}>
          {icon}
        </div>
        <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md border border-slate-100">
          <Loader2 className={`w-4 h-4 animate-spin ${
            isSpace ? 'text-cyan-500' : isOffice ? 'text-violet-500' : 'text-amber-500'
          }`} />
        </div>
      </div>

      {/* Title & Status */}
      <div className="text-center space-y-1.5 max-w-md">
        <h3 className="text-base font-bold text-slate-800">
          {message || (isSpace ? 'Завантаження геопросторового модуля...' : isOffice ? 'Завантаження цифрового офісу...' : 'Завантаження модуля...')}
        </h3>
        <p className="text-xs text-slate-500 font-mono">
          {isSpace ? 'Ініціалізація орбітальних шарів та геоаналітики' : isOffice ? 'Підключення бази первинних документів та реєстрів' : 'Оптимізація та підготовка інтерфейсу'}
        </p>
      </div>

      {/* Skeleton placeholders */}
      <div className="w-full max-w-xl space-y-3 pt-2">
        <div className="h-4 bg-slate-200/70 rounded-full w-3/4 mx-auto animate-pulse" />
        <div className="grid grid-cols-3 gap-3 pt-2">
          <div className="h-20 bg-slate-100 rounded-xl border border-slate-200/50 animate-pulse" />
          <div className="h-20 bg-slate-100 rounded-xl border border-slate-200/50 animate-pulse" />
          <div className="h-20 bg-slate-100 rounded-xl border border-slate-200/50 animate-pulse" />
        </div>
        <div className="h-28 bg-slate-100/80 rounded-xl border border-slate-200/50 animate-pulse mt-3" />
      </div>
    </div>
  );
};

export default PortalLoadingSkeleton;
